import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import isBefore from 'date-fns/isBefore';
import isEqual from 'date-fns/isEqual';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { faChevronRight } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { Timestamp } from '@components/basics/Timestamp';
import { Button } from '@components/main-components/button/Button';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { trackClickEvent } from '@lib/analytics';
import { SPACE } from '@utils/constants';

export const NotificationWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="group relative z-base flex min-h-[40px] w-full items-center gap-0.5 py-4 text-base-m after:absolute after:inset-0 after:-left-2 after:-right-2 after:z-below after:rounded-[6px]  after:content-[''] after:hover:bg-base50 md:min-h-[48px] md:border-b md:border-b-base200 lt:py-3  lt:text-base-l">
      {children}
    </div>
  );
};

export const Indicator = ({ className }: { className?: string }) => (
  <div
    className={twMerge(
      'absolute inline-block h-2 w-2 rounded-full bg-fuchsia500 align-super',
      className,
    )}
  />
);

export const NotificationCollectButton = ({
  onClick,
  disabled,
  loading,
  releaseURI,
  releaseId,
  sourceComponent,
}: {
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabled?: boolean;
  loading?: boolean;
  releaseURI: string;
  releaseId: string;
  sourceComponent: CollectButtonSource;
}) => (
  <>
    <Button
      label="Collect"
      iconOnly
      size="squared"
      className="flex h-9 w-9 min-w-[36px] p-0 font-semibold lt:hidden [&_.loading-circle]:h-4 [&_.loading-circle]:w-4"
      loading={loading}
      disabled={disabled}
      href={releaseURI}
      variant="tertiary"
      leadingIcon={<FontAwesomeIcon icon={faChevronRight} />}
    />

    <Button
      label="Collect"
      variant="tertiary"
      size="XS"
      className="hidden h-7 min-w-[78px] flex-shrink-0 px-1 py-2 transition-all duration-500 ease-in-out hover:bg-white hover:shadow-tertiary lt:flex"
      onClick={e => {
        trackClickEvent({
          event: 'Collect Now Clicked',
          properties: {
            sourceComponent: sourceComponent,
            associationType: 'Release',
            associationId: releaseId,
          },
        });

        onClick(e);
      }}
      disabled={disabled}
      loading={loading}
    />
  </>
);

export const MobileUsername = ({ username, isArtist }: { username: string; isArtist: boolean }) => (
  <div className="flex items-center md:hidden">
    <span className="flex max-w-[120px] items-center overflow-hidden text-ellipsis whitespace-nowrap font-semibold">
      {username}
    </span>
    {isArtist && (
      <>
        {SPACE}
        <span className="block md:hidden">
          <VerifiedTooltip size={16} />
        </span>
        <span className="hidden md:block">
          <VerifiedTooltip size={18} />
        </span>
      </>
    )}
  </div>
);

export const NotificationInfo = ({
  timeStamp,
  hasRead,
}: {
  timeStamp: number;
  hasRead: boolean;
}) => (
  <div className="relative flex min-w-[40px] items-center justify-end gap-1.5 md:gap-2">
    <Timestamp timeStamp={timeStamp} className="!text-base-xs text-base400" />
    <div className="flex min-w-[8px] flex-shrink-0 items-center">
      {!hasRead && <Indicator className="relative" />}
    </div>
  </div>
);

export const NotificationLoadingSkeleton = ({ length }: { length: number }) => {
  return Array.from({ length }).map((_, i) => (
    <div
      className="flex min-h-[40px] w-full items-center justify-between py-4 lt:min-h-[48px] lt:py-3 "
      key={`NOTIFICATION-LOADING-STATE${i}`}
    >
      <div className="flex items-center gap-2">
        <div className="h-10 w-10 animate-pulse rounded-md bg-base200 lt:h-12 lt:w-12" />
        <div className="h-3.5 w-48 animate-pulse rounded-md bg-base200 lt:h-4  lt:w-80" />
      </div>
      <div className="relative flex min-w-[40px] items-center justify-end lt:min-h-[48px]">
        <div className="h-3.5 w-3.5 animate-pulse rounded-full bg-base200 lt:h-4" />
      </div>
    </div>
  ));
};

export function hasReadNotification({
  timestamp,
  previousTimestamp,
}: {
  timestamp: number;
  previousTimestamp: number;
}) {
  return previousTimestamp
    ? isBefore(timestamp, previousTimestamp) || isEqual(timestamp, previousTimestamp)
    : false;
}

export const MotionPlayBars = ({}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="mr-1 mt-1 hidden md:block"
    >
      <PlayIndicatorBars size="xs" />
    </motion.div>
  );
};

export const PlayingMotionLayoutShift = ({
  children,
  isPlaying,
  className,
}: {
  children: React.ReactNode;
  isPlaying: boolean;
  className?: string;
}) => {
  return (
    <motion.div
      layout="position"
      layoutDependency={isPlaying !== undefined ? isPlaying : false}
      transition={{ duration: 0.25 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};
